import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentRole, OrgRole } from "@/lib/auth/roles";
import { useTranslation } from "@/hooks/useTranslation";

interface RouteGuardProps {
  children: ReactNode;
  requiredRoles: OrgRole[];
  fallbackPath?: string;
}

export function RouteGuard({ children, requiredRoles, fallbackPath = "/" }: RouteGuardProps) {
  const { role, isSuperAdmin, isLoading } = useCurrentRole();
  const navigate = useNavigate();
  const { translate: t } = useTranslation();

  const isAuthorized = isSuperAdmin || (role && requiredRoles.includes(role));

  useEffect(() => {
    if (!isLoading && !isAuthorized) {
      navigate(fallbackPath);
    }
  }, [isLoading, isAuthorized, navigate, fallbackPath]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t("loading")}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
